import companyCreateFormStyles from './company-create-form.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { toErrorMessage } from '../../api/error-utils';
import { ApiUserMaintenance } from '../../api/core/api-user-maintenance';
import {
  AddGroupInput,
  LoginGroup,
  MembershipStatus,
} from '../../api/agwa-api-model';
import { write } from '../../language/lang';
import { terms } from '../../language/terms';
import {
  MEMBERSHIP_LABELS,
  MEMBERSHIP_ORDER,
} from './shared/company-status-display';

/**
 * Payload of `company-created`: the company that was created, and nothing else.
 *
 * It carries NO instruction about what comes next (D-128). It used to carry one, and the host acted
 * on it; there is now a single destination, so a second field here would be a choice the host has
 * nowhere to spend. Routing stays the host's decision, which is what keeps this form portable.
 */
export interface CompanyCreatedDetail {
  group: LoginGroup | undefined;
}

/**
 * The create-company form, on a page of its own.
 *
 * REPLACES `add-group-dialog`, on the architect's ruling of 2026-09-04 (D-089), made while
 * watching the dialog fail live: "too much info to fit well in a dialog. better as a full
 * route". The form carries five fields, a mandatory question, and two standing warnings -
 * which is the repository's own definition of a routed page, not a dialog. The move also
 * retires a traced hazard for free: the dialog's hoisted membership dropdown could sit
 * stuck over the footer buttons, intercepting the very click that submits.
 *
 * THE MEMBERSHIP QUESTION IS A RADIO GROUP, not a dropdown - the same ruling. Three
 * mutually exclusive answers to a mandatory question belong in the open, where choosing
 * one is a single click and no popup can cover anything.
 *
 * PORTABLE per the component rules: no router import - the host decides what "created"
 * and "cancelled" mean by listening to `company-created` (detail: the new group) and
 * `create-cancelled`.
 */
@customElement('company-create-form')
export class CompanyCreateForm extends LitElement {
  static styles = unsafeCSS(companyCreateFormStyles);

  @state() private loading = false;
  @state() private name = '';
  @state() private active = true;
  /**
   * Deliberately starts empty rather than on a value: the server requires a choice on create, and
   * pre-selecting one would answer AGWA's question for them with the answer nobody checks.
   */
  @state() private membershipStatus: MembershipStatus | '' = '';
  @state() private userNamePrefix = '';
  @state() private saveError: string | null = null;
  @state() private imisReference = '';

  private handleNameChange(e: CustomEvent) {
    this.name = (e.target as HTMLInputElement).value;
  }
  private handleActiveChange(e: CustomEvent) {
    this.active = (e.target as HTMLInputElement).checked;
  }
  private handleMembershipStatusChange(e: CustomEvent) {
    this.membershipStatus = (e.target as HTMLInputElement)
      .value as MembershipStatus;
  }
  private handleUserNamePrefixChange(e: CustomEvent) {
    // Prefixes are lowercase and limited to letters, numbers, hyphen and underscore (no dots -
    // the dot separates the prefix from the username - and no spaces). Sanitise as typed to match
    // the rule the server enforces.
    const input = e.target as HTMLInputElement;
    const sanitised = input.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (input.value !== sanitised) {
      input.value = sanitised;
    }
    this.userNamePrefix = sanitised;
  }

  private handleImisReferenceChange(e: CustomEvent) {
    const value = (e.target as HTMLInputElement).value;
    if (value.length <= 100) {
      this.imisReference = value;
    }
  }

  private async handleSave() {
    this.saveError = null;
    this.loading = true;
    try {
      const input: AddGroupInput = {
        name: this.name,
        active: this.active,
        membershipStatus: this.membershipStatus || undefined,
        userNamePrefix: this.userNamePrefix,
        imisDetails: {
          reference: this.imisReference,
          properties: {}, // always empty for now
        },
      };
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const result = await api.addGroup(input);
      if (!result.success) {
        // The refusal is shown and the form keeps everything that was typed - a page
        // that navigated away on refusal would lose the whole form, the same rule the
        // old dialog enforced by staying open.
        // No `replaceTerms` here: the API client resolves term tags for every failed
        // response, so a screen that forgot to would still read correctly.
        this.saveError = toErrorMessage(result);
        this.scrollTop = 0;
        return;
      }
      this.dispatchEvent(
        new CustomEvent<CompanyCreatedDetail>('company-created', {
          detail: {
            group: result.data?.group,
          },
          bubbles: true,
          composed: true,
        })
      );
    } catch (err: unknown) {
      this.saveError =
        (err as Error)?.message || 'An unexpected error occurred.';
      this.scrollTop = 0;
    } finally {
      this.loading = false;
    }
  }

  private handleCancel() {
    this.dispatchEvent(
      new CustomEvent('create-cancelled', { bubbles: true, composed: true })
    );
  }

  render() {
    return html`
      <div class="create-form-main">
        ${
          this.saveError
            ? html`<sl-alert variant="danger" open>${this.saveError}</sl-alert>`
            : null
        }
        <section>
          <h3>${terms.loginGroup.value} Details</h3>
          <sl-input
            label="Name"
            value=${this.name}
            @sl-input=${this.handleNameChange}
            required
            placeholder="Enter ${terms.loginGroup.lower} name"
          ></sl-input>
          <sl-input
            label="${terms.loginUser.value} Name Prefix"
            value=${this.userNamePrefix}
            @sl-input=${this.handleUserNamePrefixChange}
            required
            placeholder="e.g. member1"
            help-text="Lowercase letters, numbers, hyphen (-) or underscore (_) - no spaces or dots. Every ${terms.loginUser.lower} for this ${terms.loginGroup.lower} is prefixed with it: prefix 'member1' makes ${terms.loginUser.lower} 'john.doe' into 'member1.john.doe'."
          ></sl-input>
          <sl-alert variant="warning" open>
            <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
            <strong>Important:</strong> The ${terms.loginUser.value} Name Prefix
            is required and must be unique. It cannot be changed once any
            ${terms.loginUser.lower} exists for the ${terms.loginGroup.value},
            because every username is tied to it.
          </sl-alert>
          <sl-checkbox
            ?checked=${this.active}
            @sl-change=${this.handleActiveChange}
            class="active-checkbox"
          >
            Active
          </sl-checkbox>

          <!--
            Membership is a required choice rather than a default, because the answer that would
            be defaulted is wrong more often than it is right, and nobody revisits a field they
            were never asked. Three options rather than the tickbox AGWA sketched: a company that
            has lapsed is not the same as one that never joined, and only the three-way choice can
            record the difference. It governs nothing operational - whether the company may work
            is the Active control above.

            A RADIO GROUP, not a dropdown (architect, 2026-09-04): the three answers sit in the
            open where choosing one is a single click, nothing pops over anything, and an
            unanswered question is VISIBLY unanswered rather than hidden behind a closed select.
          -->
          <sl-radio-group
            label=${`Is this ${terms.loginGroup.lower} a ${terms.member.lower}?`}
            value=${this.membershipStatus}
            required
            class="membership-choice"
            @sl-change=${this.handleMembershipStatusChange}
            help-text="Recorded for reporting and for how far this ${terms.loginGroup.lowerPossessive} data may travel. It does not affect whether they can use the system."
          >
            <!--
              Options come from the same map the list and the legend read, so the word AGWA picks
              here is the word they see on the row afterwards. Hard-coding them again is how a
              choice comes to be labelled one thing on the form and another in the table.
            -->
            ${MEMBERSHIP_ORDER.map(
              (status) =>
                html`<sl-radio value=${status}
                  >${MEMBERSHIP_LABELS[status]}</sl-radio
                >`
            )}
          </sl-radio-group>
        </section>

        <section>
          <h3>${terms.imis.value} Details</h3>
          <sl-input
            label="Reference"
            value=${this.imisReference}
            maxlength="100"
            @sl-input=${this.handleImisReferenceChange}
            placeholder="Enter ${terms.imis.value} reference"
            required
          ></sl-input>
        </section>

        <section>
          <h3>Security &amp; Access</h3>
          <!--
            THE ORDER IS NOW THE ONLY ROUTE THERE IS (D-128, architect 2026-09-18). This note
            always described company, then sites; the form also carried a tickbox that jumped a
            successful save straight to site creation, so it described an order a person could
            skip. The tickbox is gone and staff is named in the sequence, because staff is who the
            roles in the first sentence are granted to, and it is the step the shortcut hid.
          -->
          <sl-alert variant="primary" open>
            ${write`Security roles and features follow the
              ${terms.userGroupDomain.lowerPlural} registered on each
              ${terms.loginSite.lower}. Create the ${terms.loginGroup.value}
              first, then add its ${terms.staffUser.lowerPlural}, then its
              ${terms.loginSite.lowerPlural} and register their
              ${terms.userGroupDomain.lowerPlural}.`}
          </sl-alert>
        </section>

        <div class="create-form-footer">
          <sl-button
            variant="neutral"
            @click=${this.handleCancel}
            ?disabled=${this.loading}
            >Cancel</sl-button
          >
          <sl-button
            variant="primary"
            @click=${this.handleSave}
            ?loading=${this.loading}
            ?disabled=${this.loading}
          >
            <sl-icon slot="prefix" name="plus"></sl-icon>
            Add ${terms.loginGroup.value}
          </sl-button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'company-create-form': CompanyCreateForm;
  }
}
